<template>
  <div class="crossing-config">
    <!-- Step Indicator -->
    <div class="step-indicator mb-4">
      <div class="step" :class="{ active: drawingPhase === 'video', completed: drawingPhase !== 'video' && videoPoints.length >= 3 }">
        <span class="step-number">1</span>
        <span class="step-label">Video</span>
      </div>
      <div v-if="currentMode === 'crosswalk'" class="step" :class="{ active: drawingPhase === 'map', completed: drawingPhase === 'complete' }">
        <span class="step-number">2</span>
        <span class="step-label">Map</span>
      </div>
      <div class="step" :class="{ active: drawingPhase === 'complete' || (currentMode === 'waiting' && videoPoints.length >= 3) }">
        <span class="step-number">{{ currentMode === 'crosswalk' ? '3' : '2' }}</span>
        <span class="step-label">Save</span>
      </div>
    </div>

    <!-- Drawing State Box -->
    <div class="drawing-state-box mb-4" :class="drawingPhase">
      <template v-if="drawingPhase === 'video'">
        <div class="state-header">
          <b-icon icon="video" size="is-small"></b-icon>
          <span class="ml-2">Step 1: Drawing on Video</span>
        </div>
        <p class="state-description">Click points on the video to define the polygon shape.</p>
        <div class="state-actions">
          <span class="point-count">Points: <strong>{{ videoPoints.length }}</strong></span>
          <b-button size="is-small" type="is-warning" @click="undoLastPoint" :disabled="videoPoints.length === 0">
            Undo
          </b-button>
          <b-button size="is-small" type="is-danger" @click="resetDrawing">
            Reset
          </b-button>
        </div>
      </template>
      <template v-else-if="drawingPhase === 'map'">
        <div class="state-header map-phase">
          <b-icon icon="map" size="is-small"></b-icon>
          <span class="ml-2">Step 2: Drawing on Map</span>
        </div>
        <p class="state-description">Click the same {{ videoPoints.length }} points on the map in order.</p>
        <div class="state-actions">
          <span class="point-count">Points: <strong>{{ mapPoints.length }} / {{ videoPoints.length }}</strong></span>
          <b-button size="is-small" type="is-warning" @click="undoLastPoint" :disabled="mapPoints.length === 0">
            Undo
          </b-button>
          <b-button size="is-small" type="is-danger" @click="resetDrawing">
            Start Over
          </b-button>
        </div>
      </template>
      <template v-else>
        <div class="state-header complete-phase">
          <b-icon icon="check-circle" size="is-small"></b-icon>
          <span class="ml-2">Step 3: Ready to Save</span>
        </div>
        <p class="state-description success-text">All {{ videoPoints.length }} points matched! Review and save.</p>
        <b-button size="is-small" type="is-danger" @click="resetDrawing">
          Start Over
        </b-button>
      </template>
    </div>

    <!-- Zone Type Toggle -->
    <div class="zone-type-toggle mb-4">
      <h3 class="section-title">Zone Type</h3>
      <div class="mode-buttons">
        <b-button
          :type="currentMode === 'crosswalk' ? 'is-primary' : 'is-light'"
          @click="setMode('crosswalk')"
        >
          Crosswalk
        </b-button>
        <b-button
          :type="currentMode === 'waiting' ? 'is-primary' : 'is-light'"
          @click="setMode('waiting')"
        >
          Waiting Area
        </b-button>
      </div>
    </div>

    <!-- Crosswalk Options -->
    <div v-if="currentMode === 'crosswalk'" class="zone-options mb-4">
      <b-field label="Zone Name *" label-position="on-border">
        <b-input v-model="zoneForm.name" placeholder="e.g., Crosswalk_1" size="is-small"></b-input>
      </b-field>
      <div class="columns is-mobile is-multiline is-variable is-1">
        <div class="column is-6-tablet is-12-mobile ml-0 pl-0">
          <b-field label="Stage Name *" label-position="on-border">
            <b-input v-model="zoneForm.stageName" placeholder="e.g., North Crossing" size="is-small"></b-input>
          </b-field>
        </div>
        <div class="column is-6-tablet is-12-mobile mr-0 pr-0">
          <b-field label="Stage ID" label-position="on-border">
            <b-input v-model="zoneForm.stageId" placeholder="e.g., north_crossing" size="is-small"></b-input>
          </b-field>
        </div>
      </div>
      <b-field label="Orientation">
        <div class="orientation-buttons">
          <b-button
            size="is-small"
            :type="zoneForm.orientation === 'horizontal' ? 'is-info' : 'is-light'"
            @click="zoneForm.orientation = 'horizontal'"
          >
            Horizontal
          </b-button>
          <b-button
            size="is-small"
            :type="zoneForm.orientation === 'vertical' ? 'is-info' : 'is-light'"
            @click="zoneForm.orientation = 'vertical'"
          >
            Vertical
          </b-button>
        </div>
      </b-field>
      <b-field label="Number of Lines *" label-position="on-border">
        <b-input v-model.number="zoneForm.numLines" type="number" min="10" max="50" size="is-small"></b-input>
      </b-field>
    </div>

    <!-- Waiting Area Options -->
    <div v-if="currentMode === 'waiting'" class="zone-options mb-4">
      <b-field label="Waiting Area Name" label-position="on-border">
        <b-input v-model="waitingForm.name" placeholder="e.g., Sidewalk_Wait_1" size="is-small"></b-input>
      </b-field>
      <b-field label="Associated Crosswalk Zone" label-position="on-border">
        <b-select v-model="waitingForm.crosswalkZoneId" placeholder="Select zone" size="is-small" expanded>
          <option :value="null">-- Select zone --</option>
          <option v-for="zone in crosswalkConfig.zones" :key="zone.id" :value="zone.id">
            {{ zone.name }}
          </option>
        </b-select>
      </b-field>
      <b-field label="Direction Label" label-position="on-border">
        <b-input
          v-model="waitingForm.label"
          placeholder="A, B, C..."
          maxlength="2"
          size="is-small"
          style="text-transform: uppercase; font-weight: bold;"
        ></b-input>
      </b-field>
      <p class="help">Used for origin/destination tracking (A→B)</p>
    </div>

    <!-- Action Button -->
    <div class="action-button mb-4">
      <b-button
        type="is-primary"
        expanded
        :disabled="getActionButtonDisabled()"
        @click="nextStep"
      >
        {{ getActionButtonText() }}
      </b-button>
    </div>

    <hr class="section-divider">

    <!-- Saved Zones List -->
    <div class="saved-zones mb-4">
      <h3 class="section-title">
        Saved Zones ({{ crosswalkConfig.zones.length }})
      </h3>
      <div v-if="crosswalkConfig.zones.length === 0" class="empty-state">
        No zones defined yet
      </div>
      <div v-for="(zone, index) in crosswalkConfig.zones" :key="zone.id" class="zone-item">
        <div class="zone-header">
          <span class="zone-color" :style="{ background: getZoneColor(index) }"></span>
          <span class="zone-name">{{ zone.name }}</span>
          <span v-if="zone.geoPolygon" class="badge geo-badge">GEO</span>
        </div>
        <div class="zone-info">
          {{ zone.polygon.length }} pts | {{ zone.lines ? zone.lines.length : 0 }} lines | {{ zone.orientation }}
          <span v-if="zone.stageName"><br>Stage: {{ zone.stageName }}</span>
        </div>
        <div class="zone-actions">
          <b-button size="is-small" type="is-danger" @click="deleteZone(index)">Delete</b-button>
        </div>
      </div>
    </div>

    <!-- Saved Waiting Areas List -->
    <div class="saved-waiting mb-4">
      <h3 class="section-title">
        Saved Waiting Areas ({{ crosswalkConfig.waitingAreas.length }})
      </h3>
      <div v-if="crosswalkConfig.waitingAreas.length === 0" class="empty-state">
        No waiting areas defined yet
      </div>
      <div v-for="(area, index) in crosswalkConfig.waitingAreas" :key="area.id" class="zone-item waiting">
        <div class="zone-header">
          <span class="zone-color" :style="{ background: getWaitingColor(index) }"></span>
          <span class="zone-name">{{ area.name }}</span>
          <span v-if="area.label" class="badge waiting-badge">{{ area.label }}</span>
          <span v-if="area.geoPolygon" class="badge geo-badge">GEO</span>
        </div>
        <div class="zone-info">
          {{ area.polygon.length }} pts
          <span v-if="area.crosswalkZoneId"> | Zone: {{ getZoneName(area.crosswalkZoneId) }}</span>
        </div>
        <div class="zone-actions">
          <b-button size="is-small" type="is-danger" @click="deleteWaitingArea(index)">Delete</b-button>
        </div>
      </div>
    </div>

    <hr class="section-divider">

    <!-- Direction Mapping -->
    <div class="direction-mapping mb-4">
      <h3 class="section-title">Direction Mapping</h3>
      <p class="help mb-2">Define direction names (e.g., Northbound) for each crosswalk zone.</p>

      <div v-if="getDirectionMappingZones().length === 0" class="empty-state">
        Add labeled waiting areas (A, B) linked to zones to configure directions
      </div>

      <div v-for="zoneData in getDirectionMappingZones()" :key="zoneData.zoneId" class="direction-zone-card">
        <h4 class="direction-zone-title">
          <span class="zone-color" :style="{ background: getZoneColorById(zoneData.zoneId) }"></span>
          {{ zoneData.zone.name }}
        </h4>
        <div v-for="dir in zoneData.directions" :key="dir" class="direction-row">
          <span class="direction-label">{{ dir }}</span>
          <b-input
            :value="crosswalkConfig.directionMap[zoneData.zoneId] ? crosswalkConfig.directionMap[zoneData.zoneId][dir] : ''"
            @input="updateDirectionName(zoneData.zoneId, dir, $event)"
            placeholder="e.g., Northbound..."
            size="is-small"
          ></b-input>
        </div>
      </div>
    </div>

    <hr class="section-divider">

    <!-- Output JSON -->
    <div class="output-section">
      <h3 class="section-title">Output JSON</h3>
      <b-field>
        <b-input
          type="textarea"
          :value="JSON.stringify(crosswalkConfig, null, 2)"
          readonly
          rows="10"
          size="is-small"
        ></b-input>
      </b-field>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrossingConfig',
  props: {
    videoSrcWidth: {
      type: Number,
      default: 0
    },
    videoSrcHeight: {
      type: Number,
      default: 0
    },
    canvasWidth: {
      type: Number,
      default: 0
    },
    canvasHeight: {
      type: Number,
      default: 0
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    initialConfig: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      // Drawing state
      drawingPhase: 'video', // 'video', 'map', 'complete'
      videoPoints: [], // [[x, y], ...] relative coords (0-1)
      mapPoints: [], // [[lng, lat], ...]
      currentMode: 'crosswalk', // 'crosswalk' or 'waiting'

      // Zone colors
      ZONE_COLORS: ['#00ff00', '#0080ff', '#00ffff', '#80ff00', '#00ff80', '#0040ff'],
      WAITING_COLORS: ['#ff8000', '#ff0080', '#ffff00', '#ff00ff', '#ff4000', '#ff0040'],

      // ID counters
      currentZoneId: 1,
      currentWaitingId: 1,

      // Forms
      zoneForm: {
        name: '',
        stageName: '',
        stageId: '',
        orientation: 'horizontal',
        numLines: 10
      },
      waitingForm: {
        name: '',
        label: '',
        crosswalkZoneId: null
      },

      // Main config data
      crosswalkConfig: {
        zones: [],
        waitingAreas: [],
        directionMap: {}
      }
    }
  },
  mounted() {
    if (this.initialConfig) {
      this.loadConfig(this.initialConfig)
    }

    // Listen for keyboard shortcuts
    document.addEventListener('keydown', this.handleKeyboard)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboard)
  },
  methods: {
    // Drawing methods
    addVideoPoint(relX, relY) {
      if (this.drawingPhase !== 'video') return

      this.videoPoints.push([
        parseFloat(relX.toFixed(6)),
        parseFloat(relY.toFixed(6))
      ])

      this.$emit('videoPointsChanged', this.videoPoints, this.getCurrentColor())
    },

    addMapPoint(lng, lat) {
      if (this.drawingPhase !== 'map') return
      if (this.mapPoints.length >= this.videoPoints.length) return

      this.mapPoints.push([lng, lat])

      this.$emit('mapPointsChanged', this.mapPoints, this.getCurrentColor())

      if (this.mapPoints.length === this.videoPoints.length) {
        this.drawingPhase = 'complete'
      }
    },

    undoLastPoint() {
      if (this.drawingPhase === 'video' && this.videoPoints.length > 0) {
        this.videoPoints.pop()
        this.$emit('videoPointsChanged', this.videoPoints, this.getCurrentColor())
      } else if (this.drawingPhase === 'map' && this.mapPoints.length > 0) {
        this.mapPoints.pop()
        this.$emit('mapPointsChanged', this.mapPoints, this.getCurrentColor())
      }
    },

    resetDrawing() {
      this.videoPoints = []
      this.mapPoints = []
      this.drawingPhase = 'video'
      this.$emit('drawingReset')
      this.$emit('switchView', 'video')
    },

    nextStep() {
      if (this.drawingPhase === 'video' && this.videoPoints.length >= 3) {
        if (this.currentMode === 'waiting') {
          // Waiting areas don't require map points, save directly
          this.saveZone()
        } else {
          // Crosswalk zones require map points
          this.drawingPhase = 'map'
          this.mapPoints = []
          this.$emit('switchView', 'map')
        }
      } else if (this.drawingPhase === 'complete') {
        this.saveZone()
      }
    },

    // Zone management
    saveZone() {
      console.log('saveZone - videoPoints at start:', JSON.stringify(this.videoPoints))
      if (this.videoPoints.length < 3) {
        this.$buefy.toast.open({
          message: 'Cannot save: need at least 3 points',
          type: 'is-danger'
        })
        return
      }

      // Crosswalk zones require map points, waiting areas don't
      if (this.currentMode === 'crosswalk' && this.mapPoints.length !== this.videoPoints.length) {
        this.$buefy.toast.open({
          message: 'Cannot save: map points must match video points',
          type: 'is-danger'
        })
        return
      }

      // Validate name is provided
      if (this.currentMode === 'crosswalk' && !this.zoneForm.name.trim()) {
        this.$buefy.toast.open({
          message: 'Please enter a Zone Name before saving',
          type: 'is-danger'
        })
        return
      }
      if (this.currentMode === 'crosswalk' && !this.zoneForm.stageName.trim()) {
        this.$buefy.toast.open({
          message: 'Please enter a Stage Name before saving',
          type: 'is-danger'
        })
        return
      }
      if (this.currentMode === 'waiting') {
        if (!this.waitingForm.name.trim()) {
          this.$buefy.toast.open({
            message: 'Please enter a Waiting Area Name before saving',
            type: 'is-danger'
          })
          return
        }
        if (!this.waitingForm.crosswalkZoneId) {
          this.$buefy.toast.open({
            message: 'Please select a Crosswalk Zone before saving',
            type: 'is-danger'
          })
          return
        }
        if (!this.waitingForm.label || !this.waitingForm.label.trim()) {
          this.$buefy.toast.open({
            message: 'Please enter a Direction Label before saving',
            type: 'is-danger'
          })
          return
        }
      }

      const colorIndex = this.currentMode === 'crosswalk'
        ? this.crosswalkConfig.zones.length
        : this.crosswalkConfig.waitingAreas.length

      if (this.currentMode === 'crosswalk') {
        const name = this.zoneForm.name.trim()
        console.log('saveZone CROSSWALK - this.videoPoints:', JSON.stringify(this.videoPoints))
        console.log('saveZone CROSSWALK - this.mapPoints:', JSON.stringify(this.mapPoints))
        console.log('saveZone CROSSWALK - videoSrcWidth:', this.videoSrcWidth, 'videoSrcHeight:', this.videoSrcHeight)
        const zone = {
          id: this.currentZoneId++,
          name: name,
          polygon: [...this.videoPoints],
          geoPolygon: [...this.mapPoints],
          orientation: this.zoneForm.orientation,
          numLines: this.zoneForm.numLines,
          stageName: this.zoneForm.stageName.trim() || name,
          stageId: this.zoneForm.stageId.trim() || name.toLowerCase().replace(/\s+/g, '_'),
          color: this.hexToRgb(this.ZONE_COLORS[colorIndex % this.ZONE_COLORS.length])
        }
        console.log('saveZone CROSSWALK - zone.polygon after spread:', JSON.stringify(zone.polygon))
        zone.lines = this.generateLines(zone)
        console.log('saveZone CROSSWALK - zone.lines after generateLines:', JSON.stringify(zone.lines))
        this.crosswalkConfig.zones.push(zone)

        // Clear form
        this.zoneForm.name = ''
        this.zoneForm.stageName = ''
        this.zoneForm.stageId = ''

        this.$buefy.toast.open({
          message: `Zone "${name}" saved with ${this.videoPoints.length} points!`,
          type: 'is-success'
        })
      } else {
        const name = this.waitingForm.name.trim()
        console.log('saveZone WAITING - this.videoPoints:', JSON.stringify(this.videoPoints))
        console.log('saveZone WAITING - videoSrcWidth:', this.videoSrcWidth, 'videoSrcHeight:', this.videoSrcHeight)
        const area = {
          id: this.currentWaitingId++,
          name: name,
          polygon: [...this.videoPoints],
          geoPolygon: [...this.mapPoints],
          label: this.waitingForm.label ? this.waitingForm.label.toUpperCase() : null,
          crosswalkZoneId: this.waitingForm.crosswalkZoneId,
          color: this.hexToRgb(this.WAITING_COLORS[colorIndex % this.WAITING_COLORS.length])
        }
        console.log('saveZone WAITING - area.polygon after spread:', JSON.stringify(area.polygon))
        console.log('saveZone WAITING - waiting area to save:', JSON.stringify(area))
        this.crosswalkConfig.waitingAreas.push(area)

        // Clear form
        this.waitingForm.name = ''
        this.waitingForm.label = ''
        this.waitingForm.crosswalkZoneId = null

        this.$buefy.toast.open({
          message: `Waiting Area "${name}" saved!`,
          type: 'is-success'
        })
      }

      this.resetDrawing()
      this.emitConfigChanged()
    },

    deleteZone(index) {
      const zone = this.crosswalkConfig.zones[index]
      if (zone) {
        // Remove direction map entries for this zone
        delete this.crosswalkConfig.directionMap[zone.id]
      }
      this.crosswalkConfig.zones.splice(index, 1)
      this.emitConfigChanged()
      this.$emit('redrawRequired')
    },

    deleteWaitingArea(index) {
      const area = this.crosswalkConfig.waitingAreas[index]
      if (area && area.crosswalkZoneId && area.label) {
        // Clean up direction map entries that reference this waiting area
        this.cleanupDirectionMapForZone(area.crosswalkZoneId)
      }
      this.crosswalkConfig.waitingAreas.splice(index, 1)
      // After deletion, re-check if the zone still has enough waiting areas for direction mapping
      if (area && area.crosswalkZoneId) {
        this.cleanupDirectionMapForZone(area.crosswalkZoneId)
      }
      this.emitConfigChanged()
      this.$emit('redrawRequired')
    },

    cleanupDirectionMapForZone(zoneId) {
      // Get remaining waiting areas for this zone
      const remainingAreas = this.crosswalkConfig.waitingAreas.filter(
        a => a.crosswalkZoneId === zoneId && a.label
      )
      const remainingLabels = remainingAreas.map(a => a.label)

      // If less than 2 waiting areas remain, remove entire direction map for this zone
      if (remainingLabels.length < 2) {
        delete this.crosswalkConfig.directionMap[zoneId]
        return
      }

      // Otherwise, clean up invalid direction entries
      const dirMap = this.crosswalkConfig.directionMap[zoneId]
      if (dirMap) {
        const validKeys = []
        for (let i = 0; i < remainingLabels.length; i++) {
          for (let j = 0; j < remainingLabels.length; j++) {
            if (i !== j) {
              validKeys.push(`${remainingLabels[i]} to ${remainingLabels[j]}`)
            }
          }
        }
        // Remove invalid keys
        Object.keys(dirMap).forEach(key => {
          if (!validKeys.includes(key)) {
            delete dirMap[key]
          }
        })
        // If no valid entries remain, remove the entire map
        if (Object.keys(dirMap).length === 0) {
          delete this.crosswalkConfig.directionMap[zoneId]
        }
      }
    },

    // Line generation
    generateLines(zone) {
      if (zone.polygon.length < 3) return []
      if (!zone.numLines || zone.numLines <= 0) return []

      // Convert relative coords to pixel coords for calculation
      const polygon = zone.polygon.map(p => [
        Math.round(p[0] * this.videoSrcWidth),
        Math.round(p[1] * this.videoSrcHeight)
      ])

      const xs = polygon.map(p => p[0])
      const ys = polygon.map(p => p[1])
      const minX = Math.min(...xs), maxX = Math.max(...xs)
      const minY = Math.min(...ys), maxY = Math.max(...ys)

      const lines = []
      const numLines = zone.numLines

      if (zone.orientation === 'horizontal') {
        const step = (maxY - minY) / (numLines + 1)
        for (let i = 0; i < numLines; i++) {
          const y = Math.round(minY + (i + 1) * step)
          const ints = this.findIntersections('h', y, polygon, minX, maxX)
          if (ints.length >= 2) {
            ints.sort((a, b) => a - b)
            lines.push([[ints[0], y], [ints[ints.length - 1], y]])
          }
        }
      } else {
        const step = (maxX - minX) / (numLines + 1)
        for (let i = 0; i < numLines; i++) {
          const x = Math.round(minX + (i + 1) * step)
          const ints = this.findIntersections('v', x, polygon, minY, maxY)
          if (ints.length >= 2) {
            ints.sort((a, b) => a - b)
            lines.push([[x, ints[0]], [x, ints[ints.length - 1]]])
          }
        }
      }

      // Convert back to relative coords
      return lines.map(line => [
        [line[0][0] / this.videoSrcWidth, line[0][1] / this.videoSrcHeight],
        [line[1][0] / this.videoSrcWidth, line[1][1] / this.videoSrcHeight]
      ])
    },

    findIntersections(dir, val, polygon, min, max) {
      const intersections = []
      const n = polygon.length
      for (let i = 0; i < n; i++) {
        const p1 = polygon[i], p2 = polygon[(i + 1) % n]
        const [a1, b1] = dir === 'h' ? [p1[1], p1[0]] : [p1[0], p1[1]]
        const [a2, b2] = dir === 'h' ? [p2[1], p2[0]] : [p2[0], p2[1]]
        if ((a1 <= val && val <= a2) || (a2 <= val && val <= a1)) {
          if (a1 !== a2) {
            const t = (val - a1) / (a2 - a1)
            const result = Math.round(b1 + t * (b2 - b1))
            if (result >= min && result <= max) intersections.push(result)
          }
        }
      }
      return [...new Set(intersections)]
    },

    // Direction mapping
    getDirectionMappingZones() {
      const zoneLabels = {}
      this.crosswalkConfig.waitingAreas.forEach(area => {
        if (area.label && area.crosswalkZoneId) {
          if (!zoneLabels[area.crosswalkZoneId]) {
            zoneLabels[area.crosswalkZoneId] = []
          }
          zoneLabels[area.crosswalkZoneId].push(area.label)
        }
      })

      return Object.entries(zoneLabels)
        .filter(([zoneId, labels]) => labels.length >= 2)
        .map(([zoneId, labels]) => {
          const zone = this.crosswalkConfig.zones.find(z => z.id === parseInt(zoneId))
          const sortedLabels = labels.sort()
          const directions = []
          for (let i = 0; i < sortedLabels.length; i++) {
            for (let j = 0; j < sortedLabels.length; j++) {
              if (i !== j) {
                directions.push(`${sortedLabels[i]} to ${sortedLabels[j]}`)
              }
            }
          }
          return {
            zoneId: parseInt(zoneId),
            zone: zone,
            labels: sortedLabels,
            directions: directions
          }
        })
        .filter(item => item.zone)
    },

    updateDirectionName(zoneId, direction, name) {
      if (!this.crosswalkConfig.directionMap[zoneId]) {
        this.$set(this.crosswalkConfig.directionMap, zoneId, {})
      }
      if (name && name.trim()) {
        this.$set(this.crosswalkConfig.directionMap[zoneId], direction, name.trim())
      } else {
        this.$delete(this.crosswalkConfig.directionMap[zoneId], direction)
      }
      this.emitConfigChanged()
    },

    // Mode management
    setMode(mode) {
      if (this.currentMode !== mode) {
        this.currentMode = mode
        this.resetDrawing()
        this.$emit('modeChanged', mode)
        // Redraw saved zones after mode change
        this.$nextTick(() => {
          this.$emit('redrawRequired')
        })
      }
    },

    // Helpers
    getCurrentColor() {
      const colorIndex = this.currentMode === 'crosswalk'
        ? this.crosswalkConfig.zones.length
        : this.crosswalkConfig.waitingAreas.length
      return this.currentMode === 'crosswalk'
        ? this.ZONE_COLORS[colorIndex % this.ZONE_COLORS.length]
        : this.WAITING_COLORS[colorIndex % this.WAITING_COLORS.length]
    },

    getZoneColor(index) {
      return this.ZONE_COLORS[index % this.ZONE_COLORS.length]
    },

    getWaitingColor(index) {
      return this.WAITING_COLORS[index % this.WAITING_COLORS.length]
    },

    getZoneColorById(zoneId) {
      const index = this.crosswalkConfig.zones.findIndex(z => z.id === zoneId)
      return index >= 0 ? this.getZoneColor(index) : '#888'
    },

    getZoneName(zoneId) {
      const zone = this.crosswalkConfig.zones.find(z => z.id === zoneId)
      return zone ? zone.name : 'Unknown'
    },

    getActionButtonText() {
      if (this.drawingPhase === 'video') {
        if (this.currentMode === 'waiting') {
          return 'Prepare Waiting Area'
        }
        return 'Next: Draw on Map →'
      } else if (this.drawingPhase === 'map') {
        return `Waiting for ${this.videoPoints.length - this.mapPoints.length} more points...`
      } else {
        return 'Prepare Zone'
      }
    },

    getActionButtonDisabled() {
      if (this.drawingPhase === 'video') {
        if (this.currentMode === 'waiting') {
          // Waiting areas save directly from video phase, check all required fields
          return this.videoPoints.length < 3 ||
                 !this.waitingForm.name.trim() ||
                 !this.waitingForm.crosswalkZoneId ||
                 !this.waitingForm.label ||
                 !this.waitingForm.label.trim()
        }
        return this.videoPoints.length < 3
      } else if (this.drawingPhase === 'map') {
        return true
      } else {
        // In complete phase, check if required fields are filled
        if (this.currentMode === 'crosswalk') {
          return !this.zoneForm.name.trim() || !this.zoneForm.stageName.trim()
        } else {
          return !this.waitingForm.name.trim() ||
                 !this.waitingForm.crosswalkZoneId ||
                 !this.waitingForm.label ||
                 !this.waitingForm.label.trim()
        }
      }
    },

    hexToRgb(hex) {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [0, 255, 0]
    },

    rgbToHex(rgb) {
      if (typeof rgb === 'string') return rgb
      return '#' + rgb.map(x => x.toString(16).padStart(2, '0')).join('')
    },

    handleKeyboard(e) {
      // Only handle if this component is active (parent should manage this)
      if (!this.isEditable) return

      const key = e.key.toLowerCase()
      if (key === 'z' && !e.ctrlKey && !e.metaKey) {
        this.undoLastPoint()
      } else if (key === 'r' && !e.ctrlKey && !e.metaKey) {
        this.resetDrawing()
      } else if (key === 'enter') {
        // Enter to save: in complete phase for crosswalk, or in video phase for waiting areas
        if (this.drawingPhase === 'complete') {
          this.saveZone()
        } else if (this.currentMode === 'waiting' && this.drawingPhase === 'video' && this.videoPoints.length >= 3 &&
                   this.waitingForm.name.trim() && this.waitingForm.crosswalkZoneId && this.waitingForm.label && this.waitingForm.label.trim()) {
          this.saveZone()
        }
      } else if (key === 'n' && this.drawingPhase === 'video' && this.videoPoints.length >= 3) {
        this.nextStep()
      }
    },

    // Config management
    loadConfig(config) {
      if (config.zones) {
        this.crosswalkConfig.zones = config.zones.map((z, i) => ({
          ...z,
          color: z.color || this.hexToRgb(this.ZONE_COLORS[i % this.ZONE_COLORS.length])
        }))
        this.currentZoneId = Math.max(...this.crosswalkConfig.zones.map(z => z.id), 0) + 1
      }
      if (config.waitingAreas) {
        this.crosswalkConfig.waitingAreas = config.waitingAreas.map((a, i) => ({
          ...a,
          color: a.color || this.hexToRgb(this.WAITING_COLORS[i % this.WAITING_COLORS.length])
        }))
        this.currentWaitingId = Math.max(...this.crosswalkConfig.waitingAreas.map(a => a.id), 0) + 1
      }
      if (config.directionMap) {
        this.crosswalkConfig.directionMap = { ...config.directionMap }
      }

      this.$emit('redrawRequired')
    },

    getConfig() {
      return JSON.parse(JSON.stringify(this.crosswalkConfig))
    },

    emitConfigChanged() {
      this.$emit('configChanged', this.getConfig())
    },

    // Public methods for parent component
    getDrawingData() {
      return {
        drawingPhase: this.drawingPhase,
        videoPoints: this.videoPoints,
        mapPoints: this.mapPoints,
        currentMode: this.currentMode,
        currentColor: this.getCurrentColor(),
        zones: this.crosswalkConfig.zones,
        waitingAreas: this.crosswalkConfig.waitingAreas
      }
    },

    isInVideoDrawingPhase() {
      return this.drawingPhase === 'video'
    },

    isInMapDrawingPhase() {
      return this.drawingPhase === 'map'
    },

    // Called from parent when polygon is drawn on video via canvas
    setVideoPolygon(points) {
      if (!points || points.length < 3) {
        this.$buefy.toast.open({
          message: 'Polygon must have at least 3 points',
          type: 'is-warning'
        })
        return
      }

      // Set video points from the drawn polygon
      console.log('setVideoPolygon - received points:', JSON.stringify(points))
      this.videoPoints = points.map(p => [
        parseFloat(p[0].toFixed(6)),
        parseFloat(p[1].toFixed(6))
      ])
      console.log('setVideoPolygon - stored videoPoints:', JSON.stringify(this.videoPoints))

      // Emit for drawing overlay
      this.$emit('videoPointsChanged', this.videoPoints, this.getCurrentColor())

      // Waiting areas are video-only, don't switch to map
      if (this.currentMode === 'waiting') {
        // Stay in video phase, user can save directly
        return
      }

      // Clear any existing map points and switch to map phase for crosswalk
      this.mapPoints = []
      this.drawingPhase = 'map'
      this.$emit('switchView', 'map')
    },

    // Set the current mode (crosswalk or waiting) from parent
    setCurrentMode(mode) {
      if (mode === 'crosswalk' || mode === 'waiting') {
        this.currentMode = mode
      }
    },

    // Get current mode for parent
    getCurrentMode() {
      return this.currentMode
    }
  }
}
</script>

<style scoped>
.crossing-config {
  padding: 0;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.step-indicator {
  display: flex;
  gap: 10px;
}

.step {
  flex: 1;
  padding: 4px;
  border-radius: 6px;
  text-align: center;
  font-size: 0.85rem;
  background: #f5f5f5;
  color: #888;
  border: 2px solid transparent;
}

.step.active {
  background: #18AA7E;
  color: white;
  border-color: #18AA7E;
}

.step.completed {
  background: #0D322A;
  color: white;
  border-color: #0D322A;
}

.step-number {
  font-weight: bold;
  display: block;
}

.step-label {
  font-size: 0.75rem;
}

.drawing-state-box {
  background: #f9f9f9;
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 15px;
}

.drawing-state-box.video {
  border-color: #18AA7E;
}

.drawing-state-box.map {
  border-color: #18AA7E;
}

.drawing-state-box.complete {
  border-color: #0D322A;
}

.state-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #18AA7E;
  margin-bottom: 8px;
}

.state-header.map-phase {
  color: #18AA7E;
}

.state-header.complete-phase {
  color: #0D322A;
}

.state-description {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 10px;
}

.state-description.success-text {
  color: #18AA7E;
}

.state-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.point-count {
  font-size: 0.9rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: #363636;
}

.mode-buttons,
.orientation-buttons {
  display: flex;
  gap: 8px;
}

.zone-options {
  background: #fafafa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #eee;
}

.section-divider {
  border: none;
  border-top: 2px solid #eee;
  margin: 20px 0;
}

.empty-state {
  text-align: center;
  padding: 15px;
  color: #888;
  font-size: 0.85rem;
  background: #f9f9f9;
  border-radius: 6px;
}

.zone-item {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.zone-item.waiting {
  border-left: 3px solid #ff8000;
}

.zone-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.zone-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.zone-name {
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: bold;
}

.geo-badge {
  background: #9b59b6;
  color: white;
}

.waiting-badge {
  background: #f39c12;
  color: white;
}

.zone-info {
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 8px;
}

.zone-actions {
  display: flex;
  gap: 5px;
}

.direction-zone-card {
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.direction-zone-title {
  font-size: 0.9rem;
  color: #18AA7E;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.direction-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.direction-label {
  font-size: 0.85rem;
  color: #0D322A;
  min-width: 70px;
  font-weight: 600;
}

.output-section {
  margin-top: 20px;
}

.output-section textarea {
  word-break: break-all;
  max-width: 100%;
}

/* Ensure all inputs don't overflow */
.crossing-config :deep(input),
.crossing-config :deep(textarea),
.crossing-config :deep(select) {
  max-width: 100%;
  box-sizing: border-box;
}

.zone-item,
.direction-zone-card,
.zone-options,
.drawing-state-box {
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

/* Fix Bulma columns overflow */
.crossing-config :deep(.columns) {
  margin-left: 0;
  margin-right: 0;
  max-width: 100%;
}

.crossing-config :deep(.column) {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.mode-buttons,
.orientation-buttons {
  flex-wrap: wrap;
}
</style>
